import { useState, useEffect } from "react";

interface ConfettiProps {
  size?: number;
  x?: [number, number];
  y?: [number, number];
  duration?: number;
  infinite?: boolean;
  delay?: [number, number];
  colorRange?: [number, number];
  colorArray?: string[];
  amount?: number;
  iterationCount?: number | string;
  fallDistance?: string;
  rounded?: boolean;
  cone?: boolean;
  noGravity?: boolean;
  xSpread?: number;
  destroyOnComplete?: boolean;
  disableForReducedMotion?: boolean;
}

export const Confetti: React.FC<ConfettiProps> = ({
  size = 10,
  x = [-1, 1],
  y = [0.25, 1],
  duration = 2000,
  infinite = false,
  delay = [0, 50],
  colorRange = [0, 360],
  colorArray = [],
  amount = 50,
  iterationCount = 1,
  fallDistance = "100px",
  rounded = false,
  cone = false,
  noGravity = false,
  xSpread = 0,
  destroyOnComplete = true,
  disableForReducedMotion = false,
}) => {
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (!destroyOnComplete || infinite || iterationCount === "infinite") return;
    const timeout = setTimeout(
      () => setComplete(true),
      (duration + delay[1]) * (iterationCount as number)
    );
    return () => clearTimeout(timeout);
  }, [duration, delay, iterationCount, infinite, destroyOnComplete]);

  const randomBetween = (min: number, max: number) =>
    Math.random() * (max - min) + min;
  const getColor = () => {
    if (colorArray.length)
      return colorArray[Math.floor(Math.random() * colorArray.length)];
    return `hsl(${Math.floor(
      randomBetween(colorRange[0], colorRange[1])
    )}, 75%, 50%)`;
  };

  if (complete) return null;

  return (
    <div
      className={`ui-confetti-holder ${rounded ? "ui-rounded" : ""} ${
        noGravity ? "no-gravity" : ""
      }`}
    >
      <div
        className="confetti-gradient"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      ></div>
      {Array.from({ length: amount }).map((_, i) => (
        <div
          key={i}
          className="ui-confetti"
          style={
            {
              "--fall-distance": fallDistance,
              "--size": `${size}px`,
              "--color": getColor(),
              "--skew": `${randomBetween(-45, 45)}deg,${randomBetween(
                -45,
                45
              )}deg`,
              "--rotation-xyz": `${randomBetween(-10, 10)}, ${randomBetween(
                -10,
                10
              )}, ${randomBetween(-10, 10)}`,
              "--rotation-deg": `${randomBetween(0, 360)}deg`,
              "--translate-y-multiplier": randomBetween(y[0], y[1]),
              "--translate-x-multiplier": randomBetween(x[0], x[1]),
              "--scale": 0.1 * randomBetween(2, 10),
              "--transition-duration": infinite
                ? `calc(${duration}ms * var(--scale))`
                : `${duration}ms`,
              "--transition-delay": `${randomBetween(delay[0], delay[1])}ms`,
              "--transition-iteration-count": infinite
                ? "infinite"
                : iterationCount,
              "--x-spread": 1 - xSpread,
              borderRadius: rounded ? "50%" : "0",
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
};
